import { useEffect, useState } from "react";
import { Platform } from "react-native";

/**
 * Returns the browser visual viewport height (web only).
 *
 * iOS Safari resizes the visual viewport when the URL bar shows/hides after scrolling;
 * `window.innerHeight` and `useWindowDimensions()` often lag or don't match the visible
 * area, which leaves a white gap under fixed-height layouts. `visualViewport` tracks the
 * actual layout viewport and fires resize/scroll when the chrome changes.
 */
function readVisualViewportHeight(): number | null {
  if (Platform.OS !== "web" || typeof window === "undefined") {
    return null;
  }
  const vv = window.visualViewport;
  const next = vv && vv.height > 0 ? vv.height : window.innerHeight;
  return next > 0 ? next : null;
}

export function useVisualViewportHeight(): number | null {
  /** Sync initial read avoids first paint using wrong fallback height (iOS Safari URL bar / layout). */
  const [height, setHeight] = useState<number | null>(() => readVisualViewportHeight());

  useEffect(() => {
    if (Platform.OS !== "web" || typeof window === "undefined") {
      return;
    }

    const read = () => {
      setHeight(readVisualViewportHeight());
    };

    read();
    const vv = window.visualViewport;
    vv?.addEventListener("resize", read);
    vv?.addEventListener("scroll", read);
    window.addEventListener("resize", read);

    return () => {
      vv?.removeEventListener("resize", read);
      vv?.removeEventListener("scroll", read);
      window.removeEventListener("resize", read);
    };
  }, []);

  return height;
}
